import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  listPipelines,
  getPipelineDetail,
  advanceStage,
  runPipelinePrompt,
  recordAction,
  createPipeline,
} from "@/api/pipeline";

const PIPELINE_QUERY_KEY = ["pipeline"] as const;

export function usePipelines() {
  return useQuery({
    queryKey: [...PIPELINE_QUERY_KEY, "list"],
    queryFn: listPipelines,
    staleTime: 30_000,
  });
}

export function usePipelineDetail(pipelineId: string | undefined) {
  return useQuery({
    queryKey: [...PIPELINE_QUERY_KEY, "detail", pipelineId],
    queryFn: () => getPipelineDetail(pipelineId!),
    enabled: !!pipelineId,
    staleTime: 30_000,
  });
}

export function useAdvanceStage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ pipelineId, stageName }: { pipelineId: string; stageName: string }) =>
      advanceStage(pipelineId, stageName),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: PIPELINE_QUERY_KEY });
      qc.invalidateQueries({ queryKey: ["opportunities"] });
    },
  });
}

export function usePipelineRun() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      pipelineId,
      promptKind,
      stageName,
    }: {
      pipelineId: string;
      promptKind: string;
      stageName?: string;
    }) => runPipelinePrompt(pipelineId, { promptKind, stageName }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: PIPELINE_QUERY_KEY });
      qc.invalidateQueries({ queryKey: ["opportunities"] });
    },
  });
}

export function useRecordPipelineAction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      pipelineId,
      action,
    }: {
      pipelineId: string;
      action: Parameters<typeof recordAction>[1];
    }) => recordAction(pipelineId, action),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: PIPELINE_QUERY_KEY });
    },
  });
}

export function useCreatePipeline() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (opportunityId: string) => createPipeline(opportunityId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: PIPELINE_QUERY_KEY });
      qc.invalidateQueries({ queryKey: ["opportunities"] });
    },
  });
}
